import { useState } from "react";
import { Loader2, Bot, AlertCircle, Eye, EyeOff } from "lucide-react";
import { useClientAuth } from "../lib/auth-context";
import { ClientLink, useClientRouter } from "../lib/router-context";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useClientAuth();
  const { navigate } = useClientRouter();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/espace-client/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Email ou mot de passe incorrect.");
        return;
      }

      login(data.token, data.user, data.client);

      if (data.user.mustChangePassword) {
        navigate("/change-password");
      } else {
        navigate("/");
      }
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)",
    }}>
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center shadow-lg">
            <Bot className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Espace Client Jobotto</h1>
            <p className="text-sm text-white/70 mt-1">Accès réservé aux clients Jobotech</p>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl shadow-2xl overflow-hidden">
          <form onSubmit={handleLogin} className="p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-lg text-foreground">Connexion</h2>
              <p className="text-sm text-muted-foreground mt-1">Entrez vos identifiants pour accéder à votre espace.</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5" data-testid="text-client-error">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="client-email">Adresse email</label>
              <input
                id="client-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                required
                className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="input-client-email"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="client-password">Mot de passe</label>
              <div className="relative">
                <input
                  id="client-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full h-11 px-3 pr-10 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="input-client-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="text-right">
              <ClientLink to="/mot-de-passe-oublie" className="text-sm text-primary hover:underline">
                Mot de passe oublié ?
              </ClientLink>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 text-white shadow-lg"
              style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}
              data-testid="button-client-login"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Se connecter
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-white/60">
          Jobotech — Espace Client · <a href="mailto:contact@jobotto.fr" className="hover:underline text-white/80">contact@jobotto.fr</a>
        </p>
      </div>
    </div>
  );
}
